import { ProductDetails, ProductPricing } from '../types/global';

/**
 * Get pricing for display based on current UI selections
 * Filters by: currency → billing period → is_recurring
 */
export const getDisplayPricing = (
  product: ProductDetails,
  selectedCurrency: string,
  billingPeriod: 'monthly' | 'annual',
  recurringOption: 'recurring' | 'onetime'
): ProductPricing | null => {
  // Step 1: Filter by selected currency
  let filtered = product.pricing.filter(
    (p) => p.currency === selectedCurrency && p.is_active
  );

  // Step 2: Filter by billing period
  const targetBillingPeriod = billingPeriod === 'monthly' ? 'MONTHLY' : 'YEARLY';
  filtered = filtered.filter((p) => p.billing_period === targetBillingPeriod);

  // Step 3: Filter by is_recurring based on toggle
  const isRecurring = recurringOption === 'recurring';
  filtered = filtered.filter((p) => p.is_recurring === isRecurring);

  // Return first match or null
  return filtered.length > 0 ? filtered[0] : null;
};

/**
 * Get monthly price to display (actual monthly or calculated from annual)
 */
export const getMonthlyDisplayPrice = (
  product: ProductDetails,
  selectedCurrency: string,
  billingPeriod: 'monthly' | 'annual',
  recurringOption: 'recurring' | 'onetime'
): number | null => {
  const pricing = getDisplayPricing(product, selectedCurrency, billingPeriod, recurringOption);

  if (!pricing) return null;

  // For yearly billing, divide by 12 to show monthly equivalent
  if (pricing.billing_period === 'YEARLY') {
    return pricing.unit_price / 12;
  }

  return pricing.unit_price;
};

/**
 * Get annual price for display
 */
export const getAnnualDisplayPrice = (
  product: ProductDetails,
  selectedCurrency: string,
  recurringOption: 'recurring' | 'onetime'
): number | null => {
  // Get YEARLY pricing with current recurring option
  const pricing = product.pricing.find(
    (p) =>
      p.currency === selectedCurrency &&
      p.billing_period === 'YEARLY' &&
      p.is_recurring === (recurringOption === 'recurring') &&
      p.is_active
  );

  return pricing ? pricing.unit_price : null;
};

/**
 * Calculate savings percentage when comparing monthly vs annual
 */
export const getSavingsPercentage = (
  product: ProductDetails,
  selectedCurrency: string,
  recurringOption: 'recurring' | 'onetime'
): number | null => {
  const isRecurring = recurringOption === 'recurring';

  const monthlyPricing = product.pricing.find(
    (p) =>
      p.currency === selectedCurrency &&
      p.billing_period === 'MONTHLY' &&
      p.is_recurring === isRecurring &&
      p.is_active
  );

  const yearlyPricing = product.pricing.find(
    (p) =>
      p.currency === selectedCurrency &&
      p.billing_period === 'YEARLY' &&
      p.is_recurring === isRecurring &&
      p.is_active
  );

  if (!monthlyPricing || !yearlyPricing) return null;

  const monthlyPerMonth = monthlyPricing.unit_price;
  const yearlyPerMonth = yearlyPricing.unit_price / 12;

  const savings = ((monthlyPerMonth - yearlyPerMonth) / monthlyPerMonth) * 100;
  return Math.round(savings);
};

/**
 * Check if a product should show the recurring toggle
 * Returns true if both recurring and non-recurring options exist for the selected currency
 */
export const shouldShowRecurringToggle = (
  product: ProductDetails,
  selectedCurrency: string
): boolean => {
  const pricingsInCurrency = product.pricing.filter(
    (p) => p.currency === selectedCurrency && p.is_active
  );

  const hasRecurring = pricingsInCurrency.some((p) => p.is_recurring === true);
  const hasNonRecurring = pricingsInCurrency.some((p) => p.is_recurring === false);

  return hasRecurring && hasNonRecurring;
};

/**
 * Check if the recurring toggle should be disabled (grayed out)
 * Disabled if only one option is available
 */
export const isRecurringToggleDisabled = (
  product: ProductDetails,
  selectedCurrency: string
): boolean => {
  return !shouldShowRecurringToggle(product, selectedCurrency);
};

/**
 * Check if pricing is available for the selected options
 */
export const isPricingAvailable = (
  product: ProductDetails,
  selectedCurrency: string,
  billingPeriod: 'monthly' | 'annual',
  recurringOption: 'recurring' | 'onetime'
): boolean => {
  return getDisplayPricing(product, selectedCurrency, billingPeriod, recurringOption) !== null;
};

/**
 * Get currency symbol
 */
export const getCurrencySymbol = (currency: string): string => {
  const symbols: Record<string, string> = {
    USD: '$',
    EUR: '€',
    PLN: 'zł',
    JPY: '¥',
    MXN: '$',
    YEN: '¥',
  };
  return symbols[currency] || currency;
};

/**
 * Format price based on currency
 */
export const formatPrice = (price: number | null, currency: string): string => {
  if (price === null) return '0.00';

  // Japanese Yen and similar currencies don't use decimals
  const noDecimalCurrencies = ['JPY', 'YEN'];

  if (noDecimalCurrencies.includes(currency)) {
    return Math.round(price).toString();
  }

  return price.toFixed(2);
};

/**
 * Format full price with currency symbol
 */
export const formatPriceWithSymbol = (price: number | null, currency: string): string => {
  return `${getCurrencySymbol(currency)}${formatPrice(price, currency)}`;
};

/**
 * Get billing period display text
 */
export const getBillingPeriodDisplayText = (
  billingPeriod: 'ONE_TIME' | 'MONTHLY' | 'YEARLY'
): string => {
  const textMap = {
    ONE_TIME: 'one-time',
    MONTHLY: 'month',
    YEARLY: 'year',
  };
  return textMap[billingPeriod];
};

/**
 * Determine if a product is free (tier 1.0)
 */
export const isFreeProduct = (product: ProductDetails): boolean => {
  return product.tier <= 1.0;
};

/**
 * Determine if a product is custom/enterprise
 */
export const isCustomProduct = (product: ProductDetails): boolean => {
  // You may need to adjust this based on your business logic
  // For now, checking if tier is very high (e.g., >= 100)
  return product.tier >= 100;
};
